import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Modal,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFTextField,
} from "../../../../../../../RHFComponents/RHFComponents";
import { StyledDataGrid } from "../../../../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";
import { GridColDef } from "@mui/x-data-grid";
import { ReactComponent as ModifyIcon } from "../../../../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../../../assets/icons/MedintDeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../../../../../../../assets/icons/MedintAddIcon.svg";
import {
  pgmedt,
  pgmtgpq,
  profiliQuesiti,
  ricPgmtgpq,
} from "../../../../../../../../networking";
import { AllertMessage } from "../MedintAgendaFormDetail";
import {
  Action,
  Entity,
  ModalState,
} from "../../../../../../../../services/medintAgendaFormTypes";
import AttrGPModalDetails from "./AttrGPModalDetails";

type Props = {
  open: boolean;
  onClose: () => void;
  parentMethods: UseFormReturn<any>;
  rows?: any[];
  createdIdPgmedt?: string;
  onSavedPgmedtData?: () => Promise<void> | void;
};

export type PGMTGPQDATA = {
  DECODE_GPQUDIA_GPQUDIA_DESCRIZIONE: string;
  GPQUDIA_CODICE: string;
  PGMEDT_PRGINT: string;
  PGMTGPQ_DT_FIN_VAL: string;
  PGMTGPQ_DT_INI_VAL: string;
  PGMTGPQ_OP_LOGICO: string;
  PGMTGPQ_PID_ACU: string;
  PGMTGPQ_PRGINT: string;
  PGMTGPQ_SW_DISABILITATO: string;
  PGMTGPQ_TIMESTAMP: string;
};

//Tipi che gestiscono i dati che arrivano dall'api profiliQuesiti (bottone scarica default)
type QuesitoDefault = {
  GPQUDIA_CODICE: string;
  DEFGPQDD_OP_LOGICO: string;
  DECODE_GPQUDIA_GPQUDIA_DESCRIZIONE: string;
};

type ProfiloDefault = {
  DEFGPQD_CODICE: string;
  DEFGPQD_DESCRIZIONE: string;
  NMNSPE_CODICE: string;
  DECODE_NMNSPE_NMNSPE_DESCR: string;
  quesiti: QuesitoDefault[];
};

const AttrGPModal = ({
  open,
  onClose,
  parentMethods,
  onSavedPgmedtData,
  createdIdPgmedt,
}: Props) => {
  const { control } = parentMethods;

  const idPgmedt =
    parentMethods.getValues("medIntNuovoProgramma.chiave") ||
    (createdIdPgmedt as string | undefined);

  const [loading, setLoading] = useState<boolean>(false);

  const [rowsData, setRowsData] = useState<PGMTGPQDATA[]>([]);

  const [showAllert, setShowAllert] = useState(false);

  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<any>(null);

  const [isOpenModalDetails, setIsOpenModalDetails] = useState<ModalState>({
    open: false,
  });

  const [profiliOptions, setProfiliOptions] = useState<ProfiloDefault[]>([]);

  //Definisco il tipo di azione da eseguire
  //In questo modo l'allert mostra il messaggio corretto e apre la funzione apposita
  const [pendingAction, setPendingAction] = useState<
    "delete" | "downloadDefault" | null
  >(null);

  const openModal = (entity: Entity, action: Action, payload?: any) =>
    setIsOpenModalDetails({ open: true, entity, action, payload });

  const closeModal = () => setIsOpenModalDetails({ open: false });

  const getQuesitiData = async (): Promise<PGMTGPQDATA[]> => {
    setLoading(true);

    try {
      const response = await ricPgmtgpq({
        pgmedt_prgint: idPgmedt ?? "",
      });

      const raw = response?.data?.dati?.pgmtgpq;

      //Potrebbe restituire un singolo oggetto se non contiene dati, di conseguenza devo convertirlo in un array
      //Anche per gestire il caso delle cancellazioni e aggiornare la disciplina di conseguenza
      const data: PGMTGPQDATA[] = Array.isArray(raw) ? raw : [];

      setRowsData(data);
      return data;
    } catch (error) {
      console.error(error);
      setRowsData([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  //Funzione per mappare i dati in ingresso dell'api da mostrare nello StyledDataGrid
  const quesitiRows = useMemo(() => {
    return (rowsData ?? []).map((x) => ({
      id: x.PGMTGPQ_PRGINT,
      codQuesitoDiagnostico: x.GPQUDIA_CODICE ?? "",
      descrQuesitoDiagnostico: x.DECODE_GPQUDIA_GPQUDIA_DESCRIZIONE ?? "",
      operatoreLogico: x.PGMTGPQ_OP_LOGICO ?? "",
      _raw: x,
    }));
  }, [rowsData]);

  useEffect(() => {
    if (!idPgmedt) return;

    parentMethods.setValue("medIntNuovoProgramma.profilo", null);
    getQuesitiData();
    getProfiliData();
  }, [idPgmedt, parentMethods]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmDelete = (row: any) => {
    setPendingAction("delete");
    setPendingSubmit(row);
    setMessage("Confermare la cancellazione del quesito?");
    setSeverity("warning");
    setShowAllert(true);
  };

  const askConfirmDownloadDefault = () => {
    setPendingAction("downloadDefault");
    setPendingSubmit(true);
    setMessage("Confermare l'inserimento dei quesiti di default?");
    setSeverity("warning");
    setShowAllert(true);
  };

  const selectedProfilo = parentMethods.watch("medIntNuovoProgramma.profilo");

  const handleDelete = async (row: any) => {
    if (!row) return;
    setLoading(true);

    try {
      const response = await pgmtgpq({
        prgint: row,
        crud: "d",
      });

      if (response?.data?.esito !== "OK") {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ?? "Errore durante la cancellazione.",
        );
        return;
      }

      // Ricarico i quesiti rimasti
      const remainingQuesiti = await getQuesitiData();

      // Se non ci sono più quesiti, svuoto disciplina QD e operatore logico QD
      if (remainingQuesiti.length === 0) {
        const parentForm = parentMethods.getValues("medIntNuovoProgramma");

        const responsePgmedt = await pgmedt({
          crud: "u",
          prgint: idPgmedt,
          dati: {
            pgmedt: {
              PGMEDT_PRGINT: idPgmedt,
              PGMEDT_CODICE: parentForm.codProg ?? "",
              DATA_INI_VAL: parentForm.dataIni ?? "",
              DATA_FINE_VAL: parentForm.dataFin ?? "",
              PGMEDT_DESCR: parentForm.descrCompl ?? "",
              PGMEDT_DESC_RIDOT: parentForm.descrRid ?? "",
              PGMEDT_AP_ULT_ORA: parentForm.appUltFascOra ? "S" : "N",
              PGMEDT_DES_LUN: parentForm.note?.lunedi ?? "",
              PGMEDT_DES_MAR: parentForm.note?.martedi ?? "",
              PGMEDT_DES_MER: parentForm.note?.mercoledi ?? "",
              PGMEDT_DES_GIO: parentForm.note?.giovedi ?? "",
              PGMEDT_DES_VEN: parentForm.note?.venerdi ?? "",
              PGMEDT_DES_SAB: parentForm.note?.sabato ?? "",
              PGMEDT_DES_DOM: parentForm.note?.domenica ?? "",
              PGMEDT_DES_GEN: parentForm.note?.standard ?? "",
              NMNSPE_CODICE: "",
              PGMEDT_OP_LOGICO_QD: "",
              PGMEDT_NORMALIZZAZIONE: "",
              PGMEDT_SW_RAGGRUPPABILE: "",
              NMNSPE_AMM_CODICE: "",
              PGMEDT_SW_ALTRI_ORARI: "",
              PGMEDT_SW_SOLO_GP: "0",
              PGMEDT_SW_AGGIUNTIVA: "",
            },
          },
        });

        if (responsePgmedt?.data?.esito !== "OK") {
          setSeverity("error");
          setMessage(
            responsePgmedt?.data?.messaggio ??
              "Quesito eliminato, ma errore durante l'aggiornamento della disciplina.",
          );
          return;
        }

        // refresho MedintAgendaFormDetail, così si aggiorna la disciplina nel form
        await onSavedPgmedtData?.();
      }

      setSeverity("success");
      setMessage(
        response?.data?.messaggio ?? "Cancellazione avvenuta con successo.",
      );
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante la cancellazione del quesito.");
    } finally {
      setLoading(false);
      setPendingSubmit(null);
      setPendingAction(null);
    }
  };

  //Recupero i profili a cui sono associati i quesiti.
  //Popolo l'rhf autocomplete con questi profili
  const getProfiliData = async (): Promise<ProfiloDefault[]> => {
    try {
      const response = await profiliQuesiti();
      const raw = response?.data?.dati?.profili;

      const data: ProfiloDefault[] = Array.isArray(raw) ? raw : [];

      setProfiliOptions(data);
      return data;
    } catch (error) {
      console.error(error);
      setProfiliOptions([]);
      return [];
    }
  };

  //Funzione che avvia l'inserimento dei quesiti selezionati di default da un profilo e li inserisce con l'api.
  //L'api non permette un inserimento multiplo, quindi serve un ciclo for of per inserire tutti i quesiti.
  //Oltre all'inserimento, bisogna aggiornare la disciplina e l'operatore logico QD
  const handleDownloadDefault = async () => {
    if (loading || (rowsData?.length ?? 0) !== 0) return;

    const profiloSelezionato = parentMethods.getValues(
      "medIntNuovoProgramma.profilo",
    ) as ProfiloDefault | null;

    if (!profiloSelezionato) {
      setSeverity("error");
      setMessage("Selezionare un profilo prima di scaricare i default.");
      setShowAllert(true);
      return;
    }

    setLoading(true);

    try {
      const disciplinaDefault = profiloSelezionato.NMNSPE_CODICE ?? "";
      const quesitiDefault = Array.isArray(profiloSelezionato.quesiti)
        ? profiloSelezionato.quesiti
        : [];

      if (!disciplinaDefault) {
        setSeverity("error");
        setMessage(
          "Il profilo selezionato non contiene una disciplina valida.",
        );
        setShowAllert(true);
        return;
      }

      if (quesitiDefault.length === 0) {
        setSeverity("error");
        setMessage("Il profilo selezionato non contiene quesiti.");
        setShowAllert(true);
        return;
      }

      const parentForm = parentMethods.getValues("medIntNuovoProgramma");

      //Aggiorno pgmedt con la nuova disciplina
      const responsePgmedt = await pgmedt({
        crud: "u",
        prgint: idPgmedt,
        dati: {
          pgmedt: {
            PGMEDT_PRGINT: idPgmedt,
            PGMEDT_CODICE: parentForm.codProg ?? "",
            DATA_INI_VAL: parentForm.dataIni ?? "",
            DATA_FINE_VAL: parentForm.dataFin ?? "",
            PGMEDT_DESCR: parentForm.descrCompl ?? "",
            PGMEDT_DESC_RIDOT: parentForm.descrRid ?? "",
            PGMEDT_AP_ULT_ORA: parentForm.appUltFascOra ? "S" : "N",
            PGMEDT_DES_LUN: parentForm.note?.lunedi ?? "",
            PGMEDT_DES_MAR: parentForm.note?.martedi ?? "",
            PGMEDT_DES_MER: parentForm.note?.mercoledi ?? "",
            PGMEDT_DES_GIO: parentForm.note?.giovedi ?? "",
            PGMEDT_DES_VEN: parentForm.note?.venerdi ?? "",
            PGMEDT_DES_SAB: parentForm.note?.sabato ?? "",
            PGMEDT_DES_DOM: parentForm.note?.domenica ?? "",
            PGMEDT_DES_GEN: parentForm.note?.standard ?? "",
            NMNSPE_CODICE: disciplinaDefault,
            PGMEDT_OP_LOGICO_QD: "",
            PGMEDT_NORMALIZZAZIONE: "",
            PGMEDT_SW_RAGGRUPPABILE: "",
            NMNSPE_AMM_CODICE: disciplinaDefault,
            PGMEDT_SW_ALTRI_ORARI: "",
            PGMEDT_SW_SOLO_GP: "0",
            PGMEDT_SW_AGGIUNTIVA: "",
          },
        },
      });

      if (responsePgmedt?.data?.esito !== "OK") {
        setSeverity("error");
        setMessage(
          responsePgmedt?.data?.messaggio ??
            "Errore durante l'aggiornamento della disciplina del profilo selezionato.",
        );
        setShowAllert(true);
        return;
      }

      // Inserisco i quesiti di default uno per volta attraverso un ciclo for of
      for (const quesito of quesitiDefault) {
        const responseInsert = await pgmtgpq({
          crud: "c",
          dati: {
            pgmtgpq: {
              PGMEDT_PRGINT: idPgmedt,
              GPQUDIA_CODICE: quesito.GPQUDIA_CODICE ?? "",
              PGMTGPQ_SW_DISABILITATO: "0",
              PGMTGPQ_OP_LOGICO: quesito.DEFGPQDD_OP_LOGICO ?? "",
              PGMTGPQ_DT_INI_VAL: "",
              PGMTGPQ_DT_FIN_VAL: "",
              PGMTGPQ_PID_ACU: "",
              PGMTGPQ_TIMESTAMP: "",
            },
          },
        });

        if (responseInsert?.data?.esito !== "OK") {
          setSeverity("error");
          setMessage(
            responseInsert?.data?.messaggio ??
              `Errore durante l'inserimento del quesito ${quesito.GPQUDIA_CODICE}.`,
          );
          setShowAllert(true);
          return;
        }
      }

      // Recupero di nuovo i quesiti per aggiornare la tabella
      await getQuesitiData();
      //Idem per il programma in modo da visualizzare la nuova disciplina
      await onSavedPgmedtData?.();

      setSeverity("success");
      setMessage("Quesiti del profilo scaricati con successo.");
      setShowAllert(true);
    } catch (error) {
      console.error(error);
      setSeverity("error");
      setMessage("Errore durante lo scaricamento dei quesiti del profilo.");
      setShowAllert(true);
    } finally {
      setLoading(false);
      setPendingSubmit(null);
      setPendingAction(null);
    }
  };

  // Stili
  const BLUE = "#003267";

  const sxTabs = {
    mb: 2,
    minHeight: 38,
    "& .MuiTabs-indicator": {
      backgroundColor: BLUE,
      height: 3,
      borderRadius: 3,
    },
    "& .MuiTab-root": {
      minHeight: 38,
      textTransform: "none",
      px: 0,
      mr: 3,
      fontFamily: "Montserrat",
      fontWeight: "bold",
      color: "#9e9e9e",
      transition: "color .2s ease",
    },
    "& .MuiTab-root.Mui-selected": { color: BLUE },
  } as const;

  const columns: GridColDef[] = [
    {
      field: "codQuesitoDiagnostico",
      headerName: "Cod. Quesito Diagnostico",
      flex: 1,
      minWidth: 220,
      sortable: false,
    },
    {
      field: "descrQuesitoDiagnostico",
      headerName: "Descr. Quesito Diagnostico",
      flex: 2,
      minWidth: 220,
      sortable: false,
    },
    {
      field: "operatoreLogico",
      headerName: "Operatore Logico",
      flex: 1,
      minWidth: 220,
      align: "left",
      headerAlign: "left",
      sortable: false,
    },
    {
      field: "azioni",
      headerName: "Azioni",
      flex: 1,
      minWidth: 220,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <IconButton
            size="small"
            aria-label="Modifica"
            sx={{ color: "#024E88" }}
            onClick={() => openModal("attrGP", "edit", params.row._raw)}
          >
            <ModifyIcon />
          </IconButton>
          <IconButton
            size="small"
            aria-label="Modifica"
            onClick={() => askConfirmDelete(params.row._raw.PGMTGPQ_PRGINT)}
            sx={{ color: "#d32f2f" }}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
    },
  ];
  return (
    <>
      {isOpenModalDetails.open && isOpenModalDetails.entity && (
        <AttrGPModalDetails
          open={isOpenModalDetails.open}
          onClose={closeModal}
          parentMethods={parentMethods}
          initialData={
            isOpenModalDetails.action === "insert"
              ? undefined
              : isOpenModalDetails.payload
          }
          createdIdPgmedt={createdIdPgmedt}
          action={isOpenModalDetails.action}
          onSavedQuesitiData={getQuesitiData}
          onSavedPgmedtData={onSavedPgmedtData}
        />
      )}
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={(data: any) => {
          if (loading) return;

          setShowAllert(false);

          if (pendingAction === "delete") {
            handleDelete(data);
            return;
          }

          if (pendingAction === "downloadDefault") {
            handleDownloadDefault();
            return;
          }
        }}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          onClose();
        }}
      >
        <Box
          sx={{
            width: "92vw",
            maxWidth: 980,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 2,
          }}
        >
          <Tabs
            value={0}
            sx={{ px: 1, ...sxTabs }}
            TabIndicatorProps={{ sx: { height: 3 } }}
          >
            <Tab
              label="Dati associati all'agenda"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
              }}
            />
          </Tabs>

          <Box sx={{ px: 1, pt: 2, pb: 2, maxWidth: 420 }}>
            <RHFTextField
              name="medIntNuovoProgramma.codProg"
              control={control}
              label="Programma"
              fullWidth
              disabled
            />
          </Box>

          <Box
            sx={{
              mx: 1,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box sx={{ bgcolor: "background.paper" }}>
              <Tabs
                value={0}
                sx={{
                  px: 1,
                  "& .MuiTabs-indicator": {
                    backgroundColor: `${BLUE} !important`,
                  },
                }}
                TabIndicatorProps={{ sx: { height: 3 } }}
              >
                <Tab
                  label="Quesiti"
                  sx={{
                    fontFamily: "Montserrat",
                    fontWeight: 700,
                    textTransform: "none",
                    color: `${BLUE} !important`,
                  }}
                />
              </Tabs>
            </Box>
            <Box
              sx={{
                px: 2,
                pt: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  width: 350,
                  flexShrink: 0,
                }}
              >
                <RHFAutocomplete
                  name="medIntNuovoProgramma.profilo"
                  control={control}
                  label="Profili"
                  options={profiliOptions}
                  fullWidth
                  // Disabilito in caso di caricamento o se sono già presenti quesiti
                  disabled={loading || (rowsData?.length ?? 0) !== 0}
                  getOptionLabel={(option: ProfiloDefault | string) =>
                    typeof option === "string"
                      ? option
                      : `${option.DEFGPQD_CODICE ?? ""} - ${option.DEFGPQD_DESCRIZIONE ?? ""}`
                  }
                  isOptionEqualToValue={(
                    option: ProfiloDefault,
                    value: ProfiloDefault,
                  ) => option.DEFGPQD_CODICE === value?.DEFGPQD_CODICE}
                />
              </Box>

              <Button
                variant="containedCustom"
                onClick={askConfirmDownloadDefault}
                size="small"
                //Disabilito in caso di caricamento o se sono già presenti quesiti o se non ho selezionato un profilo
                disabled={
                  loading || (rowsData?.length ?? 0) !== 0 || !selectedProfilo
                }
                sx={{
                  whiteSpace: "nowrap",
                  minWidth: 150,
                  height: 34,
                  alignSelf: "center",
                }}
              >
                Scarica Default
              </Button>

              <Button
                variant="outlinedCustom"
                startIcon={<AddIcon />}
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  minWidth: 120,
                  height: 34,
                  ml: "auto",
                  alignSelf: "center",
                  alignItems: "center",
                }}
                onClick={() => openModal("attrGP", "insert")}
              >
                Inserisci
              </Button>
            </Box>
            <Box sx={{ p: 2, pt: 1.5 }}>
              <StyledDataGrid
                sx={{ minHeight: 300, overflow: "auto" }}
                autoHeight
                rows={quesitiRows ?? []}
                columns={columns}
                loading={loading}
                hideFooter
                disableSelectionOnClick
                sortingOrder={["desc", "asc", null]}
              />
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              px: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button variant="outlinedCustom" onClick={onClose} size="small">
              Indietro
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default AttrGPModal;
